/**
 * Expense Settlement View Component
 * 
 * Displays who owes whom based on split expenses
 */

import { useMemo, useState, useEffect, useRef } from 'react';
import { View, Pressable, Alert, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useIsFocused } from '@react-navigation/native';
import { doc, getDoc } from 'firebase/firestore';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, SubHeading, Caption } from '@/components/ui/Text';
import type { Expense } from '@/services/expenses';
import type { Trip } from '@/services/trips';
import { updateTrip } from '@/services/trips';
import { db } from '@/lib/firebase';
import {
  calculateExpenseSplitSummary,
  collectExpenseParticipantIds,
  type Settlement,
} from '@/utils/expenseSplitting';
import { formatCurrency } from '@/utils/currency';
import {
  canSendNudge,
  recordNudgeSent,
  getLastNudgeTime,
  confirmSettlementPaid,
  clearSettlementConfirmation,
  subscribeSettlementConfirmations,
  type SettlementConfirmation,
} from '@/services/settlements';
// NOTE: Settlement nudge push notifications require Cloud Functions with FCM
// Local notifications would notify the nudger instead of the debtor
// The email notification is handled by Cloud Function: onSettlementNudgeCreated
import { useUserStore } from '@/state/userStore';
import { getTripParticipants } from '@/utils/tripParticipants';

type ExpenseSettlementViewProps = {
  expenses: Expense[];
  trip: Trip;
  currentUserId: string;
  /** When false, skip Firestore work (e.g. Wallet tab blurred). */
  subscriptionEnabled?: boolean;
};

type SettlementMember = {
  userId: string;
  name?: string;
  email: string;
  status: 'active' | 'leftTrip';
};

export function ExpenseSettlementView({
  expenses,
  trip,
  currentUserId,
  subscriptionEnabled = true,
}: ExpenseSettlementViewProps) {
  const theme = useTheme();
  // Gate Firestore subscriptions on tab focus so we tear them down when the
  // enclosing tab (Wallet) is blurred or the app is backgrounded. Without this
  // the onSnapshot listener keeps firing after the user navigates away and
  // triggers `permission-denied` errors while security-rule context is
  // transiently unavailable, contending with the JS thread.
  const isFocused = useIsFocused();
  const profile = useUserStore((state) => state.profile);
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const [nudgingSettlements, setNudgingSettlements] = useState<Set<string>>(new Set());
  const [nudgeStates, setNudgeStates] = useState<Record<string, { canNudge: boolean; lastNudged: Date | null }>>({});
  const [settlementConfirmations, setSettlementConfirmations] = useState<Record<string, SettlementConfirmation>>({});
  const [resolvedNamesByUserId, setResolvedNamesByUserId] = useState<Record<string, string>>({});
  const ownerNameBackfillAttempted = useRef<Set<string>>(new Set());

  const settlementMembers = useMemo(() => {
    const activeParticipants = getTripParticipants(trip, currentUserId);
    const formerMemberById = new Map(
      (trip.formerMembers || [])
        .filter((member) => typeof member?.userId === 'string' && member.userId.trim().length > 0)
        .map((member) => [member.userId, member] as const),
    );
    const activeIds = new Set(activeParticipants.map((participant) => participant.userId));
    const splitParticipantIds = collectExpenseParticipantIds(expenses);
    const allIds = Array.from(new Set([...activeParticipants.map((participant) => participant.userId), ...splitParticipantIds]));

    const memberMap = new Map<string, SettlementMember>();
    for (const participant of activeParticipants) {
      memberMap.set(participant.userId, {
        userId: participant.userId,
        email: participant.email || '',
        name: participant.name,
        status: 'active',
      });
    }

    for (const userId of allIds) {
      if (memberMap.has(userId)) continue;

      const isCurrentUser = userId === currentUserId;
      memberMap.set(userId, {
        userId,
        email: isCurrentUser ? (firebaseUser?.email ?? '') : (formerMemberById.get(userId)?.email ?? ''),
        name: isCurrentUser ? profile?.name : formerMemberById.get(userId)?.name,
        status: activeIds.has(userId) ? 'active' : 'leftTrip',
      });
    }

    return Array.from(memberMap.values());
  }, [trip, currentUserId, firebaseUser?.email, profile?.name, expenses]);

  useEffect(() => {
    if (trip.owner !== currentUserId) return;
    const n = profile?.name?.trim();
    if (!n) return;
    if (typeof trip.ownerName === 'string' && trip.ownerName.trim().length > 0) return;
    if (ownerNameBackfillAttempted.current.has(trip.id)) return;
    ownerNameBackfillAttempted.current.add(trip.id);
    void updateTrip(trip.id, currentUserId, { ownerName: n }).catch(() => {
      ownerNameBackfillAttempted.current.delete(trip.id);
    });
  }, [trip.id, trip.owner, trip.ownerName, currentUserId, profile?.name]);

  const userIds = settlementMembers.map((m) => m.userId);

  useEffect(() => {
    let cancelled = false;

    const loadNames = async () => {
      const unresolved = settlementMembers
        .map((member) => member.userId)
        .filter((id) => {
          if (!id) return false;
          if (resolvedNamesByUserId[id]) return false;
          if (settlementMembers.find((m) => m.userId === id)?.name) return false;
          if (id === currentUserId && profile?.name) return false;
          return true;
        });

      if (!unresolved.length) return;

      const entries = await Promise.all(
        unresolved.map(async (userId) => {
          try {
            const snap = await getDoc(doc(db, 'users', userId));
            const data = snap.data() as Record<string, any> | undefined;
            const name = typeof data?.name === 'string' ? data.name.trim() : '';
            return [userId, name] as const;
          } catch {
            return [userId, ''] as const;
          }
        }),
      );

      if (cancelled) return;

      setResolvedNamesByUserId((prev) => {
        const next = { ...prev };
        for (const [userId, name] of entries) {
          if (name) next[userId] = name;
        }
        return next;
      });
    };

    void loadNames();

    return () => {
      cancelled = true;
    };
  }, [settlementMembers, resolvedNamesByUserId, currentUserId, profile?.name]);

  // Calculate settlement summary
  const summary = useMemo(() => {
    return calculateExpenseSplitSummary(expenses, userIds);
  }, [expenses, userIds]);

  // Check nudge states for all settlements on mount
  useEffect(() => {
    const checkNudgeStates = async () => {
      if (!summary.settlements.length) return;
      
      const states: Record<string, { canNudge: boolean; lastNudged: Date | null }> = {};
      
      for (const settlement of summary.settlements) {
        const key = `${settlement.from}-${settlement.to}`;
        // Only show nudge button if current user is the one who should receive payment
        if (settlement.to === currentUserId) {
          const canNudge = await canSendNudge(trip.id, settlement.from, settlement.to);
          const lastNudged = await getLastNudgeTime(trip.id, settlement.from, settlement.to);
          states[key] = { canNudge, lastNudged };
        } else {
          states[key] = { canNudge: false, lastNudged: null };
        }
      }
      
      setNudgeStates(states);
    };
    
    checkNudgeStates();
  }, [summary.settlements, trip.id, currentUserId]);

  useEffect(() => {
    if (!subscriptionEnabled || !isFocused) return;

    const unsubscribe = subscribeSettlementConfirmations(trip.id, (confirmations) => {
      const map: Record<string, SettlementConfirmation> = {};
      for (const confirmation of confirmations) {
        const key = `${confirmation.fromUserId}-${confirmation.toUserId}`;
        map[key] = confirmation;
      }
      setSettlementConfirmations(map);
    });

    return unsubscribe;
  }, [trip.id, isFocused, subscriptionEnabled]);

  const handleNudge = async (settlement: Settlement) => {
    const key = `${settlement.from}-${settlement.to}`;
    
    // Check if can nudge
    const canNudge = await canSendNudge(trip.id, settlement.from, settlement.to);
    if (!canNudge) {
      Alert.alert(
        'Rate Limit',
        'You can only send one nudge per day. Please try again tomorrow.'
      );
      return;
    }

    setNudgingSettlements(prev => new Set(prev).add(key));
    
    try {
      const fromMember = settlementMembers.find(m => m.userId === settlement.from);
      const fromName = fromMember?.name || fromMember?.email.split('@')[0] || 'Someone';
      
      // Record nudge - this triggers the Cloud Function to send email notification
      // fromUserId must be the current user (nudger) to satisfy Firestore rules.
      // toUserId is the debtor being reminded.
      await recordNudgeSent(
        trip.id,
        currentUserId,
        settlement.from,
        settlement.amount,
        settlement.currency
      );
      
      // NOTE: Push notifications to the debtor require Cloud Functions with FCM
      // Local notifications would notify the nudger instead of the debtor
      // Email notification is sent via Cloud Function: onSettlementNudgeCreated
      
      // Update state
      setNudgeStates(prev => ({
        ...prev,
        [key]: { canNudge: false, lastNudged: new Date() },
      }));
      
      Alert.alert('Nudge Sent! ✅', 'A reminder has been sent.');
      
      // Note: Email notification would be triggered by Cloud Function
      // We'll create that separately
    } catch (error) {
      console.error('[ExpenseSettlementView] Error sending nudge:', error);
      Alert.alert('Error', 'Failed to send nudge. Please try again.');
    } finally {
      setNudgingSettlements(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleConfirmPaid = async (settlement: Settlement) => {
    const key = `${settlement.from}-${settlement.to}`;
    try {
      await confirmSettlementPaid(
        trip.id,
        settlement.from,
        settlement.to,
        settlement.amount,
        settlement.currency,
        currentUserId
      );
      Alert.alert('Payment Confirmed', `${getMemberDisplayName(settlement.from)} marked as paid.`);
    } catch (error) {
      console.error('[ExpenseSettlementView] Error confirming payment:', error);
      Alert.alert('Error', 'Failed to confirm payment. Please try again.');
    }
  };

  const handleClearConfirmation = async (settlement: Settlement) => {
    try {
      await clearSettlementConfirmation(trip.id, settlement.from, settlement.to);
      Alert.alert('Reopened', 'Settlement marked as unpaid again.');
    } catch (error) {
      console.error('[ExpenseSettlementView] Error clearing payment confirmation:', error);
      Alert.alert('Error', 'Failed to reopen settlement. Please try again.');
    }
  };

  const getMemberName = (userId: string): string => {
    const member = settlementMembers.find((m) => m.userId === userId);
    if (resolvedNamesByUserId[userId]) return resolvedNamesByUserId[userId];
    if (member) {
      if (member.name) return member.name;
      if (member.email) return member.email.split('@')[0] || 'Unknown';
    }
    if (userId === currentUserId && profile?.name) {
      return profile.name;
    }
    if (userId === trip.owner) {
      const denorm = typeof trip.ownerName === 'string' ? trip.ownerName.trim() : '';
      if (denorm) return denorm;
      return 'Trip organizer';
    }
    return 'Unknown';
  };

  const getMemberDisplayName = (userId: string): string => {
    const name = getMemberName(userId);
    return userId === currentUserId ? `${name} (You)` : name;
  };

  const isFormerMember = (userId: string): boolean => {
    const member = settlementMembers.find((candidate) => candidate.userId === userId);
    return member?.status === 'leftTrip';
  };

  const formatConfirmedAt = (value: Date | null | undefined): string => {
    if (!value) return 'just now';
    return value.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Filter out expenses that aren't split
  // Enhanced validation: check for splitType, splitAmong array, and paidBy
  const splitExpenses = expenses.filter((e) => {
    const hasValidSplit = e.split && 
                          e.split.splitType !== 'none' && 
                          e.split.splitAmong && 
                          Array.isArray(e.split.splitAmong) &&
                          e.split.splitAmong.length > 0 &&
                          e.split.paidBy;
    
    // Debug logging for troubleshooting
    if (e.split && !hasValidSplit) {
      console.log('[ExpenseSettlementView] Expense has split but invalid:', {
        expenseId: e.id,
        description: e.description,
        splitType: e.split.splitType,
        splitAmong: e.split.splitAmong,
        splitAmongLength: e.split.splitAmong?.length,
        paidBy: e.split.paidBy,
        fullSplit: e.split,
      });
    }
    
    return hasValidSplit;
  });

  // Additional debug logging
  if (expenses.length > 0) {
    console.log('[ExpenseSettlementView] Summary:', {
      totalExpenses: expenses.length,
      expensesWithSplitField: expenses.filter(e => e.split).length,
      validSplitExpenses: splitExpenses.length,
    });
  }

  if (splitExpenses.length === 0) {
    return (
      <Card>
        <View style={{ alignItems: 'center', padding: theme.spacing(3) }}>
          <Ionicons
            name="calculator-outline"
            size={48}
            color={theme.colors.textSecondary}
          />
          <SubHeading style={{ marginTop: theme.spacing(2), textAlign: 'center' }}>
            No Split Expenses
          </SubHeading>
          <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1), textAlign: 'center' }}>
            Split expenses to see settlement calculations
          </Caption>
        </View>
      </Card>
    );
  }

  return (
    <View style={{ gap: theme.spacing(3) }}>
      {/* Summary Card */}
      <Card>
        <View style={{ gap: theme.spacing(2) }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <SubHeading>Settlement Summary</SubHeading>
            <Pressable
              onPress={() =>
                Alert.alert(
                  'How settlement totals work',
                  'Combined bill total is the sum of every split expense — like stacking all receipts. It is not the same as adding up each person’s balance.\n\n' +
                    'Individual balances are net positions: what each person paid for the group minus their share, across all split expenses. Across the whole group, these balances always sum to zero.\n\n' +
                    'If someone left the trip but still has unresolved split history, they remain in balances/settlements with a “Left trip” badge so totals stay accurate.\n\n' +
                    'On your row, “Due to you” means you should get that much back in total. On someone else’s row, “Is owed …” means the group owes that person that much in total. “Owes …” means that person should pay that much toward settling the group.',
                  [{ text: 'OK' }],
                )
              }
              hitSlop={12}
              accessibilityRole="button"
              accessibilityLabel="Explain settlement summary"
            >
              <Ionicons name="information-circle-outline" size={22} color={theme.colors.textSecondary} />
            </Pressable>
          </View>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              paddingVertical: theme.spacing(1),
            }}
          >
            <Caption color={theme.colors.textSecondary}>Split Expenses</Caption>
            <ThemedText variant="bodyStrong">
              {summary.splitExpenses} of {expenses.length}
            </ThemedText>
          </View>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              paddingVertical: theme.spacing(1),
            }}
          >
            <View style={{ flex: 1, paddingRight: theme.spacing(2) }}>
              <Caption color={theme.colors.textSecondary}>Combined bill total</Caption>
              <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5), fontSize: 11, lineHeight: 15 }}>
                Sum of all split expense amounts. This is not the same as net balances below (those net out to zero across the group).
              </Caption>
            </View>
            <ThemedText variant="bodyStrong" style={{ flexShrink: 0 }}>
              {formatCurrency(summary.totalSplitAmount, summary.currency)}
            </ThemedText>
          </View>
        </View>
      </Card>

      {/* User Balances */}
      <Card>
        <SubHeading style={{ marginBottom: theme.spacing(0.5) }}>Individual Balances</SubHeading>
        <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(2), fontSize: 11, lineHeight: 15 }}>
          Each person&apos;s net across all split expenses: what they paid for the group minus their share. Totals across people sum to zero.
        </Caption>
        <Caption color={theme.colors.textSecondary} style={{ marginTop: -theme.spacing(1), marginBottom: theme.spacing(2), fontSize: 11, lineHeight: 15 }}>
          Former members with unresolved split history are kept here as <ThemedText variant="bodyStrong">Left trip</ThemedText> so balances remain accurate.
        </Caption>
        <View style={{ gap: theme.spacing(1.5) }}>
          {settlementMembers.map((member) => {
            const balance = summary.userBalances[member.userId] || 0;
            const isCurrentUser = member.userId === currentUserId;
            const isOwed = balance > 0.01;
            const owes = balance < -0.01;
            const isEven = !isOwed && !owes;
            const leftTrip = member.status === 'leftTrip';

            return (
              <View
                key={member.userId}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.colors.background,
                  borderRadius: theme.radii.md,
                }}
              >
                <View style={{ flex: 1, minWidth: 0 }}>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: theme.spacing(0.75),
                    }}
                  >
                    <ThemedText variant="body" numberOfLines={2}>
                      {getMemberDisplayName(member.userId)}
                    </ThemedText>
                    {leftTrip && (
                      <View
                        style={{
                          paddingHorizontal: theme.spacing(0.75),
                          paddingVertical: theme.spacing(0.25),
                          borderRadius: theme.radii.pill,
                          backgroundColor: theme.colors.borderMuted,
                        }}
                      >
                        <Caption color={theme.colors.textSecondary} style={{ fontSize: 10, fontWeight: '700' }}>
                          Left trip
                        </Caption>
                      </View>
                    )}
                  </View>
                </View>
                <View style={{ alignItems: 'flex-end', flexShrink: 0, maxWidth: '52%' }}>
                  {isEven ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: theme.spacing(0.5),
                      }}
                    >
                      <Ionicons
                        name="checkmark-circle"
                        size={16}
                        color={theme.colors.success || '#34C759'}
                      />
                      <ThemedText
                        variant="bodyStrong"
                        color={theme.colors.success || '#34C759'}
                      >
                        Even
                      </ThemedText>
                    </View>
                  ) : isOwed ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: theme.spacing(0.5),
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Ionicons
                        name="arrow-down-circle"
                        size={16}
                        color={theme.colors.success || '#34C759'}
                      />
                      <ThemedText
                        variant="bodyStrong"
                        color={theme.colors.success || '#34C759'}
                      >
                        {isCurrentUser
                          ? `Due to you ${formatCurrency(balance, summary.currency)}`
                          : `Is owed ${formatCurrency(balance, summary.currency)}`}
                      </ThemedText>
                    </View>
                  ) : (
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: theme.spacing(0.5),
                      }}
                    >
                      <Ionicons
                        name="arrow-up-circle"
                        size={16}
                        color={theme.colors.error || '#FF3B30'}
                      />
                      <ThemedText
                        variant="bodyStrong"
                        color={theme.colors.error || '#FF3B30'}
                      >
                        Owes {formatCurrency(Math.abs(balance), summary.currency)}
                      </ThemedText>
                    </View>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      </Card>

      {/* Settlements */}
      {summary.settlements.length > 0 && (
        <Card>
          <SubHeading style={{ marginBottom: theme.spacing(2) }}>Settlements</SubHeading>
          <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(2) }}>
            To balance all expenses, these payments should be made:
          </Caption>
          <View style={{ gap: theme.spacing(2) }}>
            {summary.settlements.map((settlement, index) => {
              const fromName = getMemberDisplayName(settlement.from);
              const toName = getMemberDisplayName(settlement.to);
              const payerVerb = settlement.from === currentUserId ? 'pay' : 'pays';
              const isCurrentUserInvolved =
                settlement.from === currentUserId || settlement.to === currentUserId;
              const settlementKey = `${settlement.from}-${settlement.to}`;
              const confirmation = settlementConfirmations[settlementKey];
              const isConfirmed = Boolean(confirmation && confirmation.status === 'confirmed');
              const fromLeftTrip = isFormerMember(settlement.from);
              const toLeftTrip = isFormerMember(settlement.to);

              return (
                <View
                  key={`${settlement.from}-${settlement.to}-${index}`}
                  style={{
                    paddingVertical: theme.spacing(2.5),
                    paddingHorizontal: theme.spacing(2),
                    backgroundColor: isCurrentUserInvolved
                      ? theme.colors.primary + '08'
                      : theme.colors.background,
                    borderRadius: theme.radii.md,
                    borderWidth: 1,
                    borderColor: isCurrentUserInvolved
                      ? theme.colors.primary + '30'
                      : theme.colors.borderMuted,
                  }}
                >
                  <View style={{ gap: theme.spacing(1.5) }}>
                    {/* Full-width who pays whom — separate row from badge/amount so names are not squeezed */}
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: theme.spacing(1),
                      }}
                    >
                      <Ionicons
                        name="arrow-forward"
                        size={16}
                        color={theme.colors.textSecondary}
                        style={{ marginTop: 3 }}
                      />
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <ThemedText variant="body">
                          <ThemedText variant="bodyStrong">{fromName}</ThemedText>
                          {' '}
                          {payerVerb}
                          {' '}
                          <ThemedText variant="bodyStrong">{toName}</ThemedText>
                        </ThemedText>
                        {(fromLeftTrip || toLeftTrip) && (
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: theme.spacing(0.75),
                              marginTop: theme.spacing(0.5),
                            }}
                          >
                            {fromLeftTrip && (
                              <View
                                style={{
                                  paddingHorizontal: theme.spacing(0.75),
                                  paddingVertical: theme.spacing(0.25),
                                  borderRadius: theme.radii.pill,
                                  backgroundColor: theme.colors.borderMuted,
                                }}
                              >
                                <Caption color={theme.colors.textSecondary} style={{ fontSize: 10, fontWeight: '700' }}>
                                  {fromName} left trip
                                </Caption>
                              </View>
                            )}
                            {toLeftTrip && (
                              <View
                                style={{
                                  paddingHorizontal: theme.spacing(0.75),
                                  paddingVertical: theme.spacing(0.25),
                                  borderRadius: theme.radii.pill,
                                  backgroundColor: theme.colors.borderMuted,
                                }}
                              >
                                <Caption color={theme.colors.textSecondary} style={{ fontSize: 10, fontWeight: '700' }}>
                                  {toName} left trip
                                </Caption>
                              </View>
                            )}
                          </View>
                        )}
                      </View>
                    </View>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                        gap: theme.spacing(1.5),
                      }}
                    >
                      {isConfirmed ? (
                        <View
                          style={{
                            paddingHorizontal: theme.spacing(1),
                            paddingVertical: theme.spacing(0.5),
                            borderRadius: theme.radii.pill,
                            backgroundColor: (theme.colors.success || '#34C759') + '20',
                            borderWidth: 1,
                            borderColor: (theme.colors.success || '#34C759') + '60',
                          }}
                        >
                          <Caption color={theme.colors.success || '#34C759'} style={{ fontSize: 11, fontWeight: '700' }}>
                            Paid
                          </Caption>
                        </View>
                      ) : (
                        <View
                          style={{
                            paddingHorizontal: theme.spacing(1),
                            paddingVertical: theme.spacing(0.5),
                            borderRadius: theme.radii.pill,
                            backgroundColor: theme.colors.warning + '20',
                            borderWidth: 1,
                            borderColor: theme.colors.warning + '60',
                          }}
                        >
                          <Caption color={theme.colors.warning} style={{ fontSize: 11, fontWeight: '700' }}>
                            Pending
                          </Caption>
                        </View>
                      )}
                      <View
                        style={{
                          paddingHorizontal: theme.spacing(2),
                          paddingVertical: theme.spacing(1),
                          backgroundColor: theme.colors.primary + '15',
                          borderRadius: theme.radii.md,
                        }}
                      >
                        <ThemedText variant="bodyStrong" color={theme.colors.primary}>
                          {formatCurrency(settlement.amount, settlement.currency)}
                        </ThemedText>
                      </View>
                    </View>
                    
                    {/* Bottom row: Helper text and nudge button */}
                    {isCurrentUserInvolved && (
                      <View
                        style={{
                          marginLeft: theme.spacing(2.5),
                          gap: theme.spacing(1.5),
                        }}
                      >
                        <Caption color={isConfirmed ? (theme.colors.success || '#34C759') : theme.colors.primary}>
                          {isConfirmed
                            ? (
                              settlement.from === currentUserId
                                ? `Payment confirmed by ${getMemberDisplayName(settlement.to)}`
                                : `${getMemberDisplayName(settlement.from)} marked as paid`
                            )
                            : (
                              settlement.from === currentUserId
                                ? 'You need to pay this'
                                : 'You will receive this'
                            )}
                        </Caption>
                        {/* Nudge button - only show if current user should receive payment */}
                        {settlement.to === currentUserId && (
                          <View
                            style={{
                              flexDirection: 'row',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: theme.spacing(1),
                            }}
                          >
                            {!isConfirmed && (
                              <>
                                <Pressable
                                  onPress={() => handleConfirmPaid(settlement)}
                                  style={({ pressed }) => ({
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: theme.spacing(0.5),
                                    paddingHorizontal: theme.spacing(1.5),
                                    paddingVertical: theme.spacing(0.75),
                                    borderRadius: theme.radii.md,
                                    backgroundColor: theme.colors.success + '15',
                                    borderWidth: 1,
                                    borderColor: theme.colors.success || '#34C759',
                                    opacity: pressed ? 0.8 : 1,
                                  })}
                                >
                                  <Ionicons name="checkmark-circle-outline" size={14} color={theme.colors.success || '#34C759'} />
                                  <Caption color={theme.colors.success || '#34C759'} style={{ fontSize: 12 }}>
                                    Confirm paid
                                  </Caption>
                                </Pressable>
                                <Pressable
                                  onPress={() => handleNudge(settlement)}
                                  disabled={!nudgeStates[settlementKey]?.canNudge || nudgingSettlements.has(settlementKey)}
                                  style={({ pressed }) => ({
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: theme.spacing(0.5),
                                    paddingHorizontal: theme.spacing(1.5),
                                    paddingVertical: theme.spacing(0.75),
                                    borderRadius: theme.radii.md,
                                    backgroundColor: nudgeStates[settlementKey]?.canNudge
                                      ? theme.colors.primary + '15'
                                      : theme.colors.background,
                                    borderWidth: 1,
                                    borderColor: nudgeStates[settlementKey]?.canNudge
                                      ? theme.colors.primary
                                      : theme.colors.borderMuted,
                                    opacity: (nudgeStates[settlementKey]?.canNudge && !pressed) ? 1 : 0.5,
                                  })}
                                >
                                  {nudgingSettlements.has(settlementKey) ? (
                                    <ActivityIndicator size="small" color={theme.colors.primary} />
                                  ) : (
                                    <>
                                      <Ionicons
                                        name="notifications-outline"
                                        size={14}
                                        color={nudgeStates[settlementKey]?.canNudge
                                          ? theme.colors.primary
                                          : theme.colors.textSecondary}
                                      />
                                      <Caption
                                        color={nudgeStates[settlementKey]?.canNudge
                                          ? theme.colors.primary
                                          : theme.colors.textSecondary}
                                        style={{ fontSize: 12 }}
                                      >
                                        Nudge
                                      </Caption>
                                    </>
                                  )}
                                </Pressable>
                              </>
                            )}
                            {isConfirmed && (
                              <Pressable
                                onPress={() => handleClearConfirmation(settlement)}
                                style={({ pressed }) => ({
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  gap: theme.spacing(0.5),
                                  paddingHorizontal: theme.spacing(1.5),
                                  paddingVertical: theme.spacing(0.75),
                                  borderRadius: theme.radii.md,
                                  backgroundColor: theme.colors.background,
                                  borderWidth: 1,
                                  borderColor: theme.colors.borderMuted,
                                  opacity: pressed ? 0.8 : 1,
                                })}
                              >
                                <Ionicons name="refresh-outline" size={14} color={theme.colors.textSecondary} />
                                <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                                  Mark unpaid
                                </Caption>
                              </Pressable>
                            )}
                          </View>
                        )}
                      </View>
                    )}
                    {isCurrentUserInvolved && isConfirmed && (
                      <Caption color={theme.colors.textSecondary} style={{ marginLeft: theme.spacing(2.5), marginTop: -theme.spacing(0.5) }}>
                        Confirmed {formatConfirmedAt(confirmation?.confirmedAt)}
                      </Caption>
                    )}
                  </View>
                </View>
              );
            })}
          </View>
        </Card>
      )}

      {summary.settlements.length === 0 && summary.splitExpenses > 0 && (
        <Card>
          <View style={{ alignItems: 'center', padding: theme.spacing(2) }}>
            <Ionicons
              name="checkmark-circle-outline"
              size={32}
              color={theme.colors.success || '#34C759'}
            />
            <ThemedText
              variant="bodyStrong"
              color={theme.colors.success || '#34C759'}
              style={{ marginTop: theme.spacing(1) }}
            >
              All expenses are balanced!
            </ThemedText>
            <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
              No settlements needed
            </Caption>
          </View>
        </Card>
      )}
    </View>
  );
}


